import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  BsShieldLockFill,
  BsCookie,
  BsFileTextFill,
  BsGlobe2,
  BsCreditCard2FrontFill,
  BsMapFill,
  BsTelephoneFill,
  BsEnvelopeFill,
} from "react-icons/bs";

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms of Service & Regulatory Framework | NGO Waters";
  }, []);

  const structuralTerms = [
    {
      icon: <BsCookie className="fs-5 text-warning" />,
      title: "Data Architecture & Cookies",
      text: "Systematic deployment of cookies is utilized to map operational pipeline metrics, secure consumer session tracking variables, and enhance B2B procurement flows in compliance with our primary Privacy Policy.",
    },
    {
      icon: (
        <BsGlobe2
          className="fs-5"
          style={{ color: "var(--brand-mineral-aqua)" }}
        />
      ),
      title: "Proprietary Intellectual Licensing",
      text: "All material components, technological designs, brand assets, and structural frameworks found across this terminal remain exclusively owned by Ponia Food And Beverage Pvt. Ltd. All corporate rights are strictly reserved.",
    },
    {
      icon: <BsCreditCard2FrontFill className="fs-5 text-success" />,
      title: "Wholesale Payment Cleardown",
      text: "Commercial and trade order processing sequences trigger fully upon successful monetary verification checks. Orders subject to balance clearance delays or failed processing loops are systematically locked.",
    },
  ];

  const fluidSpring = { type: "spring", stiffness: 45, damping: 15 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Ambient Decorative Soft Blur Layers */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "600px",
          height: "600px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-220px",
          left: "-60px",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "15%",
          right: "-80px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* Production-Grade Technical SEO / GEO / AEO Schema Bracket */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "NGO Waters Regulatory Terms and Conditions",
          description:
            "Legal operating framework, supply chain provisions, and corporate distribution compliance matrices for NGO Waters under Ponia Food And Beverage Private Limited.",
          publisher: {
            "@type": "Organization",
            name: "PONIA FOOD AND BEVERAGE PRIVATE LIMITED",
            duns: "CIN: U11043UP2024PTC213517",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Village - Subakara, Thana - Gonda",
              addressLocality: "Aligarh",
              addressRegion: "Uttar Pradesh",
              postalCode: "202123",
              addressCountry: "IN",
            },
          },
        })}
      </script>

      {/* ================= COMPLIANCE HERO HEADER ================= */}
      <section className="container py-4 position-relative z-1 mb-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span
            className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
            style={{
              backgroundColor: "rgba(26, 60, 107, 0.06)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.72rem",
              borderRadius: "6px",
            }}
          >
            Legal Operating Mandate
          </span>
          <h1
            className="display-5 fw-bold mb-3"
            style={{ color: "var(--brand-deep-blue)", letterSpacing: "-1.5px" }}
          >
            Terms & Conditions
          </h1>
          <p
            className="text-secondary small mx-auto mb-0"
            style={{ maxWidth: "600px", lineHeight: "1.7" }}
          >
            Governing structural regulatory policies, system-use restrictions,
            and industrial distribution parameters for the NGO Waters commercial
            network.
          </p>
        </motion.div>
      </section>

      {/* ================= CORE COMPLIANCE GRID ================= */}
      <section className="container mb-5 position-relative z-1">
        <div className="row g-4 justify-content-center">
          {structuralTerms.map((term, index) => (
            <div className="col-md-4" key={index}>
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{
                  y: -5,
                  boxShadow: "0px 10px 30px rgba(0,0,0,0.02)",
                }}
                className="card border-0 h-100 bg-white p-4"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.03)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center mb-3"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.012)",
                    width: "42px",
                    height: "42px",
                  }}
                >
                  {term.icon}
                </div>
                <h5
                  className="fw-bold text-dark mb-3"
                  style={{ fontSize: "1.02rem", lineHeight: "1.3" }}
                >
                  {term.title}
                </h5>
                <p
                  className="text-muted small mb-0"
                  style={{ lineHeight: "1.65" }}
                >
                  {term.text}
                </p>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= DETAILED LEGAL PROTOCOLS ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-12 col-xl-10">
            <div
              className="p-4 p-md-5 bg-white border"
              style={{ borderRadius: "24px", borderColor: "rgba(0,0,0,0.03)" }}
            >
              <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom border-light">
                <BsFileTextFill className="text-muted fs-5" />
                <h4
                  className="fw-bold mb-0 fs-5"
                  style={{ color: "var(--brand-deep-blue)" }}
                >
                  Usage Protocols & Legal Boundary Enforcements
                </h4>
              </div>

              <div
                className="d-flex flex-column gap-4 small text-secondary"
                style={{ lineHeight: "1.75" }}
              >
                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    1. System Access & Compliance Acknowledgement
                  </h6>
                  <p className="mb-0">
                    By initializing access protocols within this domain, user
                    blocks implicitly validate binding acceptance of all active
                    operational rules. If you do not maintain structural
                    alignment with these criteria, you are mandated to
                    discontinue system interactions immediately.
                  </p>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    2. Usage Restrictions & Intellectual Bounds
                  </h6>
                  <p className="mb-2">
                    System operators are strictly barred from deploying the
                    following actions across corporate source domains:
                  </p>
                  <ul className="ps-3 mb-0 text-muted">
                    <li>
                      Extracting or republication of technical structural data
                      frameworks from this portal.
                    </li>
                    <li>
                      Sublicensing or executing commercial trade arbitrage via
                      our native resources.
                    </li>
                    <li>
                      Executing unverified script mirrors, database scraping
                      loops, or content redistribution pathways.
                    </li>
                  </ul>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    3. Strategic Accountability Boundaries (Disclaimer)
                  </h6>
                  <p className="mb-0">
                    To the maximum boundaries mapped out by applicable
                    regulatory law, all industrial warranties relating to
                    operational uptime or system continuity are excluded.
                    Liability restrictions governing our physical batch
                    logistics, FSSAI sanitization audits, and regional
                    distribution coordinates operate strictly under the legal
                    umbrella of{" "}
                    <strong>PONIA FOOD AND BEVERAGE PRIVATE LIMITED</strong>,
                    established under registration benchmarks in Aligarh, Uttar
                    Pradesh.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= REGIONAL LOCATION & METRICS BOX ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-12 col-xl-10">
            <div className="row g-4">
              {/* Card Contact Metrics */}
              <div className="col-md-5">
                <div
                  className="card h-100 border-0 p-4 bg-white"
                  style={{
                    borderRadius: "24px",
                    border: "1px solid rgba(0,0,0,0.03)",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: "rgba(26, 60, 107, 0.04)",
                        width: "38px",
                        height: "38px",
                      }}
                    >
                      <BsShieldLockFill
                        style={{ color: "var(--brand-deep-blue)" }}
                      />
                    </div>
                    <h5
                      className="fw-bold mb-0 text-dark"
                      style={{ fontSize: "1.1rem" }}
                    >
                      Compliance Desk
                    </h5>
                  </div>

                  <div className="d-flex flex-column gap-3 small text-muted">
                    <div>
                      <span className="d-block text-dark fw-bold mb-1">
                        Corporate Entity:
                      </span>
                      Ponia Food And Beverage Pvt. Ltd.
                    </div>
                    <div>
                      <span className="d-block text-dark fw-bold mb-1">
                        <BsMapFill className="me-1 text-secondary" /> Production
                        Hub:
                      </span>
                      Village - Subakara, Thana - Gonda, <br />
                      Aligarh, Uttar Pradesh - 202123
                    </div>
                    <div>
                      <span className="d-block text-dark fw-bold mb-1">
                        <BsEnvelopeFill className="me-1 text-secondary" /> Email
                        Desk:
                      </span>
                      <a
                        href="mailto:support@ngowaters.com"
                        className="text-decoration-none text-dark fw-semibold"
                      >
                        support@ngowaters.com
                      </a>
                    </div>
                    <div>
                      <span className="d-block text-dark fw-bold mb-1">
                        <BsTelephoneFill className="me-1 text-secondary" />{" "}
                        Hotline Grid:
                      </span>
                      <a
                        href="tel:+917078430767"
                        className="text-decoration-none text-dark fw-semibold"
                      >
                        +91 7078430767
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Asset Wrapper */}
              <div className="col-md-7">
                <div
                  className="card h-100 border-0 overflow-hidden"
                  style={{
                    borderRadius: "24px",
                    border: "1px solid rgba(0,0,0,0.03)",
                  }}
                >
                  <div className="ratio h-100" style={{ minHeight: "260px" }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3525.595611995912!2d78.03196898704309!3d27.914380414348628!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3974a557e2d818f7%3A0xfe2bb2217f07133d!2sVikash%20Trading%20Company!5e0!3m2!1sen!2sin!4v1715755067249!5m2!1sen!2sin"
                      title="NGO Waters Regulatory Operations Hub"
                      allowFullScreen
                      loading="lazy"
                      style={{
                        border: "0",
                        filter: "grayscale(10%) contrast(95%)",
                      }}
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default TermsConditions;
